/**
 * PortalDesignStudio.jsx — Generative design page.
 *
 * Left rail: prompt + KB multi-select + format toggle + size.
 * Right rail: generation history.
 * Center: current output canvas (SVG + Raster preview + download +
 * zoom + pan + freehand annotations that get sent to refine).
 */
import { useState, useMemo, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Sparkles, Loader2, Download, RefreshCw, Image as ImageIcon, Code2, Database, Paperclip, X, ChevronLeft, ChevronRight, Send, FileText, File as FileIcon, Share2, GitCompareArrows, RotateCw } from 'lucide-react';
import { portalKnowledgeApi } from '../../services/api';
import { designApi } from '../../services/drawings';
import { NexusCard, NexusButton, NexusTextarea, NexusBadge } from '../../components/nexus-ui';
import { toast, usePortalStore } from '../../stores';
import { DesignCanvas } from '../../components/design/DesignCanvas';
import { VersionTree } from '../../components/design/VersionTree';
import { ShareDesignModal } from '../../components/design/ShareDesignModal';
import { diffSvgs } from '../../components/design/svgDiff';

const SIZES = [
  { label: 'A4 landscape', value: '1200x800' },
  { label: 'Square', value: '1024x1024' },
  { label: 'Portrait', value: '900x1400' },
];

// Extensions we accept as reference attachments. Images become visual
// references for the raster+SVG passes; docs get text-extracted and
// inlined into the prompt. CAD formats fall back to APS translation
// server-side (see backend `extract_reference_docs`).
const REF_ACCEPT = [
  'image/png', 'image/jpeg', 'image/webp',
  '.pdf', '.docx', '.xlsx', '.pptx', '.txt', '.md', '.markdown', '.csv', '.json',
  '.dxf', '.dwg', '.dgn', '.rvt', '.ifc', '.dwf', '.dwfx',
].join(',');

const isImageFile = (file) => (file?.type || '').startsWith('image/') ||
  /\.(png|jpg|jpeg|webp)$/i.test(file?.name || '');
const iconForExt = (name) => {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (['pdf', 'docx', 'txt', 'md', 'markdown', 'csv', 'json'].includes(ext)) return FileText;
  return FileIcon;
};

// Progressive SVG normaliser — takes whatever the model has streamed so
// far and tries to render it. If the closing </svg> hasn't arrived yet
// we append it so the browser doesn't refuse to render the partial tree.
const previewSvg = (buf) => {
  if (!buf) return '';
  const idx = buf.indexOf('<svg');
  if (idx < 0) return '';
  let sliced = buf.slice(idx);
  sliced = sliced.replace(/```[a-z]*\s*/gi, '').replace(/```\s*$/g, '');
  if (!sliced.includes('</svg>')) sliced += '</svg>';
  return sliced;
};

// Ensure the injected SVG scales to fit its container. Models often emit
// explicit width="1200" height="800" attributes that make the SVG render
// at its native pixel size (overflow / off-screen). We strip those and
// force width=100% + preserveAspectRatio so the drawing always fits.
const fitSvgToContainer = (svg) => {
  if (!svg || typeof svg !== 'string') return svg;
  // Insert style/attrs into the first <svg …> tag.
  return svg.replace(/<svg\b([^>]*)>/i, (_, attrs) => {
    // Drop explicit width / height so CSS wins.
    let cleaned = attrs
      .replace(/\swidth\s*=\s*(['"]).*?\1/i, '')
      .replace(/\sheight\s*=\s*(['"]).*?\1/i, '');
    // Add preserveAspectRatio if absent.
    if (!/preserveAspectRatio/i.test(cleaned)) {
      cleaned += ' preserveAspectRatio="xMidYMid meet"';
    }
    return `<svg${cleaned} width="100%" height="100%" style="max-width:100%;max-height:100%;height:auto;width:100%;display:block">`;
  });
};

const PortalDesignStudio = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [prompt, setPrompt] = useState('Modern 3BHK apartment, 1,200 sq ft, north-facing balcony, master bedroom with en-suite, open kitchen. Show room labels + dimensions.');
  const [selectedKBs, setSelectedKBs] = useState([]);
  const [format, setFormat] = useState('raster');
  const [size, setSize] = useState('1200x800');
  // Sheet count is auto-planned by the model based on the brief — no
  // longer a user knob. Kept in state only so the SSE `sheets_planned`
  // event can display the AI's decision to the user.
  const [plannedSheets, setPlannedSheets] = useState(null);   // array or null
  const [current, setCurrent] = useState(null);
  const [references, setReferences] = useState([]);   // list of {file, previewUrl}
  const [streaming, setStreaming] = useState(false);
  const [streamBuf, setStreamBuf] = useState('');
  const [stage, setStage] = useState(null);
  const [errors, setErrors] = useState([]);
  const [rasterReady, setRasterReady] = useState(false);
  const [refineText, setRefineText] = useState('');
  const [refineAttachments, setRefineAttachments] = useState([]);   // File[] snippets for refine
  const [refining, setRefining] = useState(false);
  const [historyCollapsed, setHistoryCollapsed] = useState(false);
  // Annotations drawn on the canvas — passed to /refine as JSON.
  // Kept per-generation-id so switching gens resets the markup.
  const [annotations, setAnnotations] = useState([]);
  const [canvasTool, setCanvasTool] = useState('pan');   // 'pan'|'rect'|'arrow'|'text'
  const [canvasTab, setCanvasTab] = useState('raster');
  const [shareOpen, setShareOpen] = useState(false);
  // currentVersion = which version is on screen. 0 = original, 1..N = refinements.
  // Kept in sync with the number of versions in `current` after every action.
  const [currentVersion, setCurrentVersion] = useState(0);
  const abortRef = useRef(null);
  const refInputRef = useRef(null);
  const refineInputRef = useRef(null);
  const refineAttachInputRef = useRef(null);

  const { data: kbs } = useQuery({
    queryKey: ['portal-kbs'],
    queryFn: () => portalKnowledgeApi.list().then((r) => r.data),
  });

  const { data: history } = useQuery({
    queryKey: ['design-generations'],
    queryFn: () => designApi.listGenerations().then((r) => r.data.items || []),
  });

  // Deep-link — `/portal/design-studio?gen=<id>` auto-loads that generation.
  // Runs once on mount. Sets currentVersion to whatever the URL says (or
  // the latest version if no `v=` param is present).
  useEffect(() => {
    const genId = searchParams.get('gen');
    const vStr = searchParams.get('v');
    if (!genId) return;
    (async () => {
      try {
        const { data } = await designApi.getGeneration(genId);
        setCurrent(data);
        setStreamBuf('');
        // Rehydrate planned sheets from the saved generation so the
        // "Cognara planned N sheets…" banner + per-row Generate
        // buttons remain accessible when a user reopens a historical
        // multi-sheet gen. (Live streams populate this via the SSE
        // `sheets_planned` event; deep-link users otherwise wouldn't
        // see the plan.)
        setPlannedSheets(data.sheet_plan || data.planned_sheets || null);
        const total = (data.versions || []).length;
        const v = vStr !== null ? Math.max(0, Math.min(total, Number(vStr))) : total;
        setCurrentVersion(v);
      } catch (e) {
        toast.error('Could not load the shared design — it may have been deleted.');
      }
    })();
  }, []);

  // Whenever the on-screen generation OR version changes, mirror it in
  // the URL so the address bar always represents the current view. This
  // makes deep-links + browser Share work naturally.
  useEffect(() => {
    if (!current?.id) return;
    const next = new URLSearchParams(searchParams);
    if (next.get('gen') !== current.id) next.set('gen', current.id);
    if (String(currentVersion) !== next.get('v')) next.set('v', String(currentVersion));
    setSearchParams(next, { replace: true });
  }, [current?.id, currentVersion]);

  const kbList = useMemo(() => {
    if (!kbs) return [];
    if (Array.isArray(kbs)) return kbs;
    return kbs.items || kbs.knowledge_bases || [];
  }, [kbs]);

  // Cleanup abort controller + object URLs on unmount
  useEffect(() => () => {
    try { abortRef.current?.abort(); } catch (e) { /* noop */ }
    references.forEach((r) => URL.revokeObjectURL(r.previewUrl));
  }, [references]);

  const attachReferences = (fileList) => {
    if (!fileList) return;
    const arr = Array.from(fileList).slice(0, 8);   // backend caps at 8
    const additions = arr.map((f) => ({
      file: f,
      isImage: isImageFile(f),
      // Only create object URLs for images to preview — docs don't need one.
      previewUrl: isImageFile(f) ? URL.createObjectURL(f) : null,
    }));
    setReferences((prev) => [...prev, ...additions].slice(0, 8));
  };
  const removeReference = (idx) => {
    setReferences((prev) => {
      const r = prev[idx];
      if (r?.previewUrl) URL.revokeObjectURL(r.previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  // Refine-side attachments — image snippets the user clips from the
  // canvas or drops from disk. Backend accepts up to 3 (snippet_0..2).
  const attachRefineFiles = (fileList) => {
    if (!fileList) return;
    const arr = Array.from(fileList).filter(isImageFile).slice(0, 3);
    setRefineAttachments((prev) => [...prev, ...arr].slice(0, 3));
  };
  const removeRefineFile = (idx) => setRefineAttachments((prev) => prev.filter((_, i) => i !== idx));

  const startStream = async () => {
    if (!prompt.trim()) return;
    // Reset UI
    setStreaming(true);
    setStreamBuf('');
    setPlannedSheets(null);   // clear old plan; will refill via sheets_planned event
    setStage(null);
    setErrors([]);
    setRasterReady(false);
    setCurrent(null);

    const controller = new AbortController();
    abortRef.current = controller;

    const form = new FormData();
    form.append('prompt', prompt);
    form.append('kb_ids', selectedKBs.join(','));
    form.append('output_format', format);
    form.append('size', size);
    form.append('num_sheets', '0');   // 0 = auto — model decides based on brief
    references.forEach((r, i) => {
      if (i === 0) form.append('reference', r.file);
      else form.append(`reference_${i}`, r.file);
    });

    const token = usePortalStore.getState().token;
    let genId = null;
    let svgAccum = '';

    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/design/stream`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) {
        const body = await resp.text().catch(() => '');
        throw new Error(body || `HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Parse each `event: X\ndata: {…}\n\n` frame
        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';
        for (const frame of frames) {
          if (!frame.trim()) continue;
          let event = 'message';
          let data = null;
          for (const line of frame.split('\n')) {
            if (line.startsWith('event:')) event = line.slice(6).trim();
            else if (line.startsWith('data:')) {
              try { data = JSON.parse(line.slice(5).trim()); } catch (e) { /* ignore */ }
            }
          }
          if (!data) continue;
          if (event === 'open') {
            genId = data.id;
            // Set a MINIMAL current record immediately so GenerationOutput
            // renders (showing streaming SVG buffer + planned sheets +
            // stage). Without this, if the stream dies before sheet 1
            // completes, the outer empty-state ("Your generated drawing
            // will appear here") remains visible even though the backend
            // is actively working — very confusing UX.
            setCurrent({
              id: data.id,
              status: 'processing',
              sheets: [],
              versions: [],
              prompt: prompt,
              raster_b64: null,
              svg: null,
            });
          }
          else if (event === 'stage') setStage(data.label);
          else if (event === 'svg_delta') {
            // For multi-sheet gens, feed only sheet 1's chunks into the
            // live preview buffer — later sheets appear via sheet tabs
            // after `done`. If `sheet` metadata is absent we're in the
            // legacy single-sheet path and always accumulate.
            if (!data.sheet || data.sheet === 1) {
              svgAccum += data.delta || '';
              setStreamBuf(svgAccum);
            }
          } else if (event === 'svg_done') {
            // Backend cleaned + validated the SVG — replace our raw stream buffer
            // with the canonical version so the canvas renders exactly what's saved.
            if (data.svg && (!data.sheet || data.sheet === 1)) {
              svgAccum = data.svg;
              setStreamBuf(svgAccum);
            } else {
              setStreamBuf(svgAccum);
            }
          } else if (event === 'raster_done') {
            setRasterReady(true);
          } else if (event === 'error') {
            setErrors((prev) => [...prev, `${data.phase}: ${data.detail}`]);
          } else if (event === 'sheets_planned') {
            setPlannedSheets(data.sheets || []);
            setStage(`Planning ${data.count} sheet${data.count === 1 ? '' : 's'}…`);
          } else if (event === 'sheet_start') {
            setStage(`Sheet ${data.index}/${data.total}…`);
          } else if (event === 'sheet_done') {
            // Refetch the generation so the just-completed sheet appears
            // in the grid IMMEDIATELY (the backend persists sheets[]
            // after each sheet completes — Phase 46). Without this
            // refresh, users only ever see sheet 1 mid-stream and think
            // the multi-sheet feature is broken.
            setStage(`Sheet ${data.index}/${data.total} · ready`);
            try {
              const { data: partial } = await designApi.getGeneration(data.id || genId);
              if (partial) {
                setCurrent(partial);
                setCurrentVersion(0);
              }
            } catch (e) { /* noop — will retry on done */ }
          } else if (event === 'done') {
            genId = data.id || genId;
            if (data.errors?.length) setErrors((prev) => [...prev, ...data.errors]);
            // Fetch full generation record with raster payload
            if (genId) {
              try {
                const { data: full } = await designApi.getGeneration(genId);
                setCurrent(full);
                setCurrentVersion(0);   // fresh gen = original
              } catch (e) { /* noop */ }
            }
          } else if (event === 'fatal') {
            setErrors((prev) => [...prev, `Fatal: ${data.detail}`]);
          }
        }
      }
      queryClient.invalidateQueries({ queryKey: ['design-generations'] });
      toast.success('Drawing generated');
    } catch (e) {
      if (e.name !== 'AbortError') {
        setErrors((prev) => [...prev, e.message || 'Stream failed']);
        toast.error(e.message || 'Stream failed');
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const startRefine = async () => {
    if (!current?.id || !refineText.trim()) return;
    setRefining(true);
    setStreamBuf('');
    setStage('Refining…');
    setErrors([]);
    const controller = new AbortController();
    abortRef.current = controller;
    const form = new FormData();
    form.append('prompt', refineText);
    if (annotations.length) form.append('annotations', JSON.stringify(annotations));
    // Branching: tell the backend which version this refinement is
    // branching FROM. If the user reverted to v1 and hits Refine, the
    // new version becomes a sibling of v2 (both children of v1) instead
    // of overwriting v2's history.
    form.append('from_version', String(currentVersion));
    refineAttachments.forEach((f, i) => form.append(`snippet_${i}`, f));
    const token = usePortalStore.getState().token;
    let svgAccum = '';
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/design/${current.id}/refine`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form, signal: controller.signal,
      });
      if (!resp.ok || !resp.body) throw new Error(await resp.text().catch(() => `HTTP ${resp.status}`));
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';
        for (const f of frames) {
          let ev = 'message', dat = null;
          for (const l of f.split('\n')) {
            if (l.startsWith('event:')) ev = l.slice(6).trim();
            else if (l.startsWith('data:')) { try { dat = JSON.parse(l.slice(5).trim()); } catch (_e) { /* skip */ } }
          }
          if (ev === 'stage' && dat) setStage(dat.label);
          else if (ev === 'raster_done' && dat?.raster_b64) {
            // Raster-mode refine (Feb 2026) — show the new drawing
            // as soon as Nano-Banana returns it, without waiting for
            // the full-record refetch. Force the canvas onto the
            // raster tab so the update is visible.
            setRasterReady(true);
            setCanvasTab('raster');
            setCurrent((prev) => prev ? {
              ...prev,
              raster_b64: dat.raster_b64,
              status: 'ready',
            } : prev);
          }
          else if (ev === 'done') {
            if (dat?.recovered) {
              toast.error('Refinement produced an empty result — kept previous version.');
            }
          } else if (ev === 'error' && dat) setErrors((prev) => [...prev, dat.detail]);
        }
      }
      setRefineText('');
      setRefineAttachments([]);
      setAnnotations([]);   // markups have been applied — start fresh for next round
      queryClient.invalidateQueries({ queryKey: ['design-generations'] });
      toast.success('Refinement applied');
    } catch (e) {
      if (e.name !== 'AbortError') toast.error(`Refine failed: ${e.message}`);
    } finally {
      // ALWAYS refetch the generation — even if the SSE stream was
      // aborted mid-flight — because the backend persists the new
      // version in a CancelledError safety net. Without this, users
      // hit "Cancel" or lose connection and their version-tree entry
      // silently disappears from the UI (bug reported iter 43).
      if (current?.id) {
        try {
          const { data: full } = await designApi.getGeneration(current.id);
          setCurrent(full);
          setCurrentVersion((full.versions || []).length);
        } catch (_e) { /* ignore fetch failure */ }
      }
      setRefining(false);
      abortRef.current = null;
    }
  };

  const jumpToVersion = async (v) => {
    if (!current?.id) return;
    const versions = current.versions || [];
    if (v < 0 || v > versions.length) return;
    try {
      const form = new FormData();
      form.append('to_version', String(v));
      const token = usePortalStore.getState().token;
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/design/${current.id}/revert`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
      });
      const { data: full } = await designApi.getGeneration(current.id);
      setCurrent(full);
      setStreamBuf('');
      setCurrentVersion(v);
      toast.success(v === 0 ? 'On original (v0)' : `On v${v}`);
    } catch (e) { toast.error('Revert failed'); }
  };

  const doUndo = () => {
    if (currentVersion > 0) jumpToVersion(currentVersion - 1);
  };
  const doRedo = () => {
    const total = (current?.versions || []).length;
    if (currentVersion < total) jumpToVersion(currentVersion + 1);
  };

  // Keyboard shortcuts: Ctrl/Cmd+Z = undo, Ctrl/Cmd+Shift+Z (or Ctrl+Y) = redo.
  // Only trigger when focus is NOT in a text input so typing prompts still works.
  useEffect(() => {
    const isEditable = (el) => {
      if (!el) return false;
      const tag = (el.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || el.isContentEditable;
    };
    const onKey = (e) => {
      if (!current?.id || isEditable(document.activeElement)) return;
      const meta = e.ctrlKey || e.metaKey;
      if (meta && !e.shiftKey && e.key.toLowerCase() === 'z') { e.preventDefault(); doUndo(); }
      else if (meta && ((e.shiftKey && e.key.toLowerCase() === 'z') || e.key.toLowerCase() === 'y')) { e.preventDefault(); doRedo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current?.id, currentVersion, current?.versions?.length]);

  const openHistory = async (gen) => {
    try {
      const { data } = await designApi.getGeneration(gen.id);
      setCurrent(data);
      setStreamBuf('');
      setCurrentVersion((data.versions || []).length);   // land on the latest version
      setAnnotations([]);   // reset markup for the new drawing
      setRefineAttachments([]);
      // Rehydrate the sheet plan so the per-row Generate buttons +
      // "Cognara planned N sheets…" banner work on historical gens.
      setPlannedSheets(data.sheet_plan || data.planned_sheets || null);
      // If the record has NO SVG but DOES have a raster, auto-switch to
      // the raster tab so the user actually sees the drawing that was
      // produced. This is the common failure mode for aborted streams —
      // raster completed first, SVG got cut off — and forcing the SVG
      // tab makes the canvas look blank when there's a valid raster
      // available one click away.
      // Raster-only mode (Feb 2026): prefer the raster tab whenever a
      // raster exists — refine now produces raster ONLY (no new SVG),
      // so if we defaulted to 'svg' the user would see a stale SVG
      // after a fresh refinement instead of the new drawing they just
      // asked for.
      const hasRasterAnywhere = !!data.raster_b64 || (data.sheets || []).some(s => s.raster_b64) || !!data.raster_storage_path;
      const hasTopSvg = (data.svg && data.svg.length > 100);
      const hasSheetSvg = (data.sheets || []).some(s => s.svg && s.svg.length > 100);
      if (hasRasterAnywhere) {
        setCanvasTab('raster');
      } else if (hasTopSvg || hasSheetSvg) {
        setCanvasTab('svg');
      } else {
        setCanvasTab('raster');
      }
    } catch (e) {
      toast.error('Could not load generation');
    }
  };

  // Which sheet (1-based) is currently being regenerated, plus its live
  // svg buffer + stage label. `null` when idle. Kept here (not in
  // GenerationOutput) so the handler survives a re-render and the SSE
  // response can setCurrent() on completion.
  const [regenSheet, setRegenSheet] = useState(null);   // { idx, buf, stage } | null
  const regenAbortRef = useRef(null);

  const cancelRegen = () => {
    if (regenAbortRef.current) {
      try { regenAbortRef.current.abort(); } catch { /* noop */ }
    }
    setRegenSheet(null);
  };

  const startRegenSheet = async (sheetIdx, promptOverride = '') => {
    if (!current?.id) return;
    if (regenSheet) return;   // already running
    cancelRegen();
    const controller = new AbortController();
    regenAbortRef.current = controller;
    setRegenSheet({ idx: sheetIdx, buf: '', stage: 'Starting…' });

    const token = usePortalStore.getState().token;
    const form = new FormData();
    if (promptOverride) form.append('prompt_override', promptOverride);

    try {
      const resp = await fetch(designApi.regenerateSheetUrl(current.id, sheetIdx), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) {
        const body = await resp.text().catch(() => '');
        throw new Error(body || `HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let localSvg = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';
        for (const frame of frames) {
          if (!frame.trim()) continue;
          let event = 'message';
          let data = null;
          for (const line of frame.split('\n')) {
            if (line.startsWith('event:')) event = line.slice(6).trim();
            else if (line.startsWith('data:')) {
              try { data = JSON.parse(line.slice(5).trim()); } catch { /* ignore */ }
            }
          }
          if (!data) continue;
          if (event === 'stage') {
            setRegenSheet((s) => (s ? { ...s, stage: data.label } : s));
          } else if (event === 'svg_delta') {
            localSvg += data.delta || '';
            setRegenSheet((s) => (s ? { ...s, buf: localSvg } : s));
          } else if (event === 'svg_done') {
            if (data.svg) localSvg = data.svg;
          } else if (event === 'sheet_regen_done') {
            // Replace this sheet in `current` and refresh the record.
            try {
              const { data: full } = await designApi.getGeneration(current.id);
              setCurrent(full);
            } catch { /* noop */ }
            toast.success(`Sheet ${sheetIdx} regenerated`);
          } else if (event === 'error') {
            toast.error(`${data.phase}: ${data.detail}`);
          } else if (event === 'fatal') {
            toast.error(`Fatal: ${data.detail}`);
          }
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') toast.error(e.message || 'Regenerate failed');
    } finally {
      setRegenSheet(null);
      regenAbortRef.current = null;
    }
  };

  const canDownloadSvg = current?.svg || streamBuf.includes('</svg>');
  const canDownloadRaster = current?.raster_b64 || current?.raster_storage_path || rasterReady;

  const versions = current?.versions || [];

  return (
    <div className={`grid grid-cols-1 gap-4 h-[calc(100vh-160px)] ${historyCollapsed ? 'lg:grid-cols-[380px,1fr,40px]' : 'lg:grid-cols-[380px,1fr,300px]'}`} data-testid="design-studio">
      {/* LEFT — Spec panel */}
      <NexusCard variant="glass" hover={false}>
        <div className="p-5 space-y-4 h-full overflow-y-auto">
          <div>
            <h2 className="font-heading text-lg font-semibold flex items-center gap-2 text-nexus-text-primary">
              <Sparkles size={16} style={{ color: '#F26B1F' }} />
              Design Studio
            </h2>
            <p className="text-[11.5px] text-nexus-text-tertiary mt-1">
              Turn a spec + your knowledge base into an editable engineering drawing.
            </p>
          </div>

          {/* Prompt — larger + user-resizable via CSS `resize: vertical` */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold mb-1.5 text-nexus-text-tertiary">Brief</div>
            <NexusTextarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={12}
              placeholder="e.g. 2BHK 850 sqft, east-facing, kitchen adjacent to dining, one common bath, one attached bath…"
              style={{ minHeight: 240, resize: 'vertical' }}
              data-testid="ds-prompt"
            />
          </div>

          {/* Reference files — images become visual anchors; docs (PDF /
              DOCX / XLSX / PPTX / TXT / MD / DXF / DWG / RVT etc.) get
              text-extracted and inlined into the prompt server-side. */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold mb-1.5 text-nexus-text-tertiary flex items-center gap-1">
              <Paperclip size={11} /> Reference files ({references.length}/8) — images + docs + CAD
            </div>
            {references.length > 0 && (
              <div className="grid grid-cols-4 gap-1.5 mb-2">
                {references.map((r, i) => {
                  const Icon = iconForExt(r.file.name);
                  return (
                    <div key={i} className="relative rounded-lg overflow-hidden flex items-center justify-center"
                      style={{ border: '1px solid var(--hairline)', background: 'var(--layer-1)', height: 64 }}
                      title={r.file.name}>
                      {r.isImage && r.previewUrl ? (
                        <img src={r.previewUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex flex-col items-center gap-1 p-1">
                          <Icon size={16} className="text-nexus-text-tertiary" />
                          <span className="text-[8.5px] text-nexus-text-tertiary truncate max-w-full px-1">
                            {(r.file.name || '').split('.').pop().toUpperCase()}
                          </span>
                        </div>
                      )}
                      <button
                        onClick={() => removeReference(i)}
                        className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-black/60 hover:bg-black/80 text-white"
                        data-testid={`ds-remove-ref-${i}`}>
                        <X size={9} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <button
              onClick={() => refInputRef.current?.click()}
              className="w-full rounded-lg py-2.5 flex flex-col items-center gap-1 transition-all"
              style={{ background: 'var(--layer-1)', border: '1px dashed var(--hairline)' }}
              data-testid="ds-attach-ref">
              <Paperclip size={12} className="text-nexus-text-tertiary" />
              <span className="text-[11px] text-nexus-text-tertiary">
                {references.length ? 'Add more files' : 'Attach images, PDFs, DOCX, XLSX, DXF, DWG…'}
              </span>
            </button>
            <input
              ref={refInputRef}
              type="file"
              accept={REF_ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => { attachReferences(e.target.files); e.target.value = ''; }}
            />
          </div>

          {/* KB selector */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold mb-1.5 text-nexus-text-tertiary flex items-center gap-1">
              <Database size={11} /> Style knowledge bases
            </div>
            <div className="space-y-1 max-h-40 overflow-y-auto rounded-lg p-2"
              style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
              {kbList.length === 0 ? (
                <div className="text-[11.5px] text-nexus-text-tertiary p-2">
                  No knowledge bases yet — the generation will proceed with sensible defaults.
                </div>
              ) : kbList.map((kb) => (
                <label key={kb.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-nexus-bg-tertiary cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedKBs.includes(kb.id)}
                    onChange={(e) => {
                      setSelectedKBs((prev) => e.target.checked ? [...prev, kb.id] : prev.filter((x) => x !== kb.id));
                    }}
                    className="accent-[#F26B1F]"
                    data-testid={`ds-kb-${kb.id}`}
                  />
                  <span className="text-[12px] text-nexus-text-primary flex-1 truncate">{kb.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Format toggle — HIDDEN (Feb 2026): raster-only mode. SVG
              and 'both' paths retired from the UI; state stays at
              'raster' by default and refine/regen operate on raster
              via Nano-Banana. Component kept as an intentional no-op
              comment so future re-enablement is a small revert. */}
          {false && (
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold mb-1.5 text-nexus-text-tertiary">Output</div>
            <div className="grid grid-cols-3 gap-1.5">
              {[
                { v: 'both', label: 'Both', icon: Sparkles },
                { v: 'svg', label: 'SVG', icon: Code2 },
                { v: 'raster', label: 'Raster', icon: ImageIcon },
              ].map(({ v, label, icon: Icon }) => (
                <button
                  key={v}
                  onClick={() => setFormat(v)}
                  className="rounded-lg px-2 py-2 flex flex-col items-center gap-1 transition-all"
                  style={{
                    background: format === v ? 'rgba(242,107,31,0.15)' : 'var(--layer-1)',
                    border: `1px solid ${format === v ? '#F26B1F' : 'var(--hairline)'}`,
                    color: format === v ? '#F26B1F' : 'var(--text-primary)',
                  }}
                  data-testid={`ds-format-${v}`}>
                  <Icon size={13} />
                  <span className="text-[10.5px] font-semibold">{label}</span>
                </button>
              ))}
            </div>
          </div>
          )}

          {/* Size */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold mb-1.5 text-nexus-text-tertiary">Size</div>
            <div className="grid grid-cols-3 gap-1.5">
              {SIZES.map((s) => (
                <button
                  key={s.value}
                  onClick={() => setSize(s.value)}
                  className="rounded-lg px-2 py-2 text-[10.5px] font-medium transition-all"
                  style={{
                    background: size === s.value ? 'rgba(242,107,31,0.15)' : 'var(--layer-1)',
                    border: `1px solid ${size === s.value ? '#F26B1F' : 'var(--hairline)'}`,
                    color: size === s.value ? '#F26B1F' : 'var(--text-primary)',
                  }}
                  data-testid={`ds-size-${s.value}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Generate */}
          {streaming ? (
            <NexusButton
              fullWidth
              variant="danger"
              onClick={() => abortRef.current?.abort()}
              data-testid="ds-cancel">
              Cancel generation
            </NexusButton>
          ) : (
            <NexusButton
              fullWidth
              disabled={!prompt.trim()}
              onClick={startStream}
              data-testid="ds-generate">
              <Sparkles size={13} /> Generate drawing
            </NexusButton>
          )}
          <p className="text-[10px] text-nexus-text-tertiary text-center">
            Streams live · Cognara Apex vector + raster engines · No timeouts.
          </p>
        </div>
      </NexusCard>

      {/* CENTER — Preview canvas */}
      <NexusCard variant="glass" hover={false}>
        <div className="p-5 h-full flex flex-col">
          <StageBanner streaming={streaming || refining} stage={stage} rasterReady={rasterReady} errors={errors} plannedSheets={plannedSheets} current={current} onRegenSheet={startRegenSheet} regenSheet={regenSheet} />
          {(!streaming && !refining && !current && !streamBuf && !plannedSheets) ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center gap-2">
              <Sparkles size={32} className="text-nexus-text-tertiary opacity-60" />
              <p className="text-[14px] text-nexus-text-primary">Your generated drawing will appear here</p>
              <p className="text-[11.5px] text-nexus-text-tertiary max-w-md">
                Write a brief on the left, optionally attach a reference image + KBs, hit <b>Generate drawing</b>.
              </p>
            </div>
          ) : (
            <>
              {/* Branching version tree — v0 = original, v1..vN = refinements.
                  Nodes with the same parent_version render as siblings. */}
              {current && versions.length > 0 && (
                <VersionTree
                  versions={versions} currentVersion={currentVersion}
                  onJump={jumpToVersion} onUndo={doUndo} onRedo={doRedo}
                />
              )}
              <div className="order-2 flex-1 flex flex-col min-h-0">
                <GenerationOutput
                  gen={current} streamBuf={streamBuf}
                  streaming={streaming || refining}
                  annotations={annotations} setAnnotations={setAnnotations}
                  tool={canvasTool} setTool={setCanvasTool}
                  tab={canvasTab} setTab={setCanvasTab}
                  onShare={() => setShareOpen(true)}
                  regenSheet={regenSheet}
                  onRegenSheet={startRegenSheet}
                  onCancelRegen={cancelRegen}
                />
              </div>

              {/* Refinement chat — flex order-1 so it appears ABOVE
                  GenerationOutput (which is order-2) in the visual
                  layout while remaining below it in JSX. Guarantees
                  the refine bar is always visible in the viewport
                  before the canvas takes over. */}
              {current && (
                <div className="order-1 mb-3 rounded-lg p-3" style={{ background: 'var(--layer-1)', border: '1.5px solid rgba(242,107,31,0.45)' }}>
                  <div className="text-[10.5px] uppercase tracking-wider font-semibold mb-1.5 flex items-center gap-2" style={{ color: '#F26B1F' }}>
                    <span className="flex items-center gap-1"><Sparkles size={11} /> Refine this drawing</span>
                    {annotations.length > 0 && (
                      <NexusBadge variant="accent" size="xs" data-testid="ds-refine-markup-count">
                        {annotations.length} markup{annotations.length === 1 ? '' : 's'} on canvas
                      </NexusBadge>
                    )}
                    {refineAttachments.length > 0 && (
                      <NexusBadge variant="default" size="xs">
                        {refineAttachments.length} snippet{refineAttachments.length === 1 ? '' : 's'}
                      </NexusBadge>
                    )}
                  </div>

                  {refineAttachments.length > 0 && (
                    <div className="flex gap-1.5 mb-2">
                      {refineAttachments.map((f, i) => (
                        <div key={i} className="relative rounded overflow-hidden"
                          style={{ border: '1px solid var(--hairline)', width: 44, height: 44 }}>
                          <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                          <button onClick={() => removeRefineFile(i)}
                            className="absolute top-0 right-0 p-0.5 bg-black/70 text-white rounded-bl"
                            data-testid={`ds-refine-remove-${i}`}>
                            <X size={8} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2 items-end">
                    <button
                      onClick={() => refineAttachInputRef.current?.click()}
                      className="p-2 rounded-md flex-shrink-0"
                      style={{ background: 'var(--layer-0)', border: '1px solid var(--hairline)' }}
                      title="Attach a drawing snippet or reference image"
                      data-testid="ds-refine-attach">
                      <Paperclip size={13} className="text-nexus-text-tertiary" />
                    </button>
                    <input
                      ref={refineAttachInputRef}
                      type="file" accept="image/*" multiple className="hidden"
                      onChange={(e) => { attachRefineFiles(e.target.files); e.target.value = ''; }}
                    />
                    <textarea
                      ref={refineInputRef}
                      value={refineText}
                      onChange={(e) => setRefineText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); startRefine(); } }}
                      placeholder={streaming ? 'Waiting for the current generation to finish…' : 'e.g. Move the master bedroom to the north, add a study nook, remove the balcony…'}
                      rows={2}
                      className="flex-1 px-3 py-2 rounded-md text-[12.5px] outline-none focus:ring-2 text-nexus-text-primary resize-none disabled:opacity-50"
                      style={{ background: 'var(--layer-0)', border: '1px solid var(--hairline)' }}
                      disabled={refining || streaming}
                      data-testid="ds-refine-input"
                    />
                    <NexusButton onClick={startRefine} isLoading={refining} disabled={!refineText.trim() || streaming} data-testid="ds-refine-send">
                      <Send size={13} /> {refining ? 'Refining' : 'Refine'}
                    </NexusButton>
                  </div>
                  <p className="text-[10px] text-nexus-text-tertiary mt-1">
                    Draw rectangles / arrows / notes on the canvas → mention them in your prompt (e.g. &quot;make the highlighted room bigger&quot;).
                    Attach snippet images for visual references. Enter to send.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </NexusCard>

      {/* RIGHT — history (collapsible) */}
      <NexusCard variant="glass" hover={false}>
        {historyCollapsed ? (
          <button onClick={() => setHistoryCollapsed(false)}
            className="h-full w-full flex items-center justify-center text-nexus-text-tertiary hover:text-nexus-accent-orange"
            data-testid="ds-history-expand">
            <ChevronLeft size={16} />
          </button>
        ) : (
          <div className="p-4 h-full flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-[13px] font-semibold text-nexus-text-primary">History</h3>
              <div className="flex items-center gap-1">
                <NexusButton size="xs" variant="ghost" onClick={() => queryClient.invalidateQueries({ queryKey: ['design-generations'] })}>
                  <RefreshCw size={11} />
                </NexusButton>
                <button onClick={() => setHistoryCollapsed(true)}
                  className="p-1 rounded hover:bg-nexus-bg-tertiary text-nexus-text-tertiary"
                  data-testid="ds-history-collapse">
                  <ChevronRight size={13} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto space-y-2" data-testid="ds-history">
            {(history || []).length === 0 ? (
              <p className="text-[11.5px] text-nexus-text-tertiary">Nothing yet — your generations appear here.</p>
            ) : (history || []).map((gen) => (
              <button
                key={gen.id}
                onClick={() => openHistory(gen)}
                className="w-full text-left p-2.5 rounded-lg transition-all hover:opacity-90"
                style={{
                  background: current?.id === gen.id ? 'rgba(242,107,31,0.1)' : 'var(--layer-1)',
                  border: `1px solid ${current?.id === gen.id ? '#F26B1F' : 'var(--hairline)'}`,
                }}
                data-testid={`ds-history-${gen.id}`}>
                <div className="text-[11.5px] text-nexus-text-primary line-clamp-2">{gen.prompt}</div>
                <div className="flex items-center justify-between mt-1.5">
                  <NexusBadge variant="default" size="xs">{gen.status || gen.output_format}</NexusBadge>
                  <span className="text-[10px] text-nexus-text-tertiary">
                    {new Date(gen.created_at).toLocaleDateString()}
                  </span>
                </div>
              </button>
            ))}
            </div>
          </div>
        )}
      </NexusCard>

      {/* Share modal — mounted at root so it overlays the whole studio. */}
      <ShareDesignModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        gen={current}
        onPublished={(updated) => setCurrent(updated)}
      />
    </div>
  );
};

const StageBanner = ({ streaming, stage, rasterReady, errors, plannedSheets, current, onRegenSheet, regenSheet }) => {
  // Sheet plan can be BIG (6 richly described sheets ≈ 60 % of the
  // viewport). Default it to collapsed once we have >=3 sheets so the
  // canvas + refine bar stay visible; user can expand for the full
  // rundown any time.
  const [planCollapsed, setPlanCollapsed] = useState(false);
  useEffect(() => {
    if (plannedSheets && plannedSheets.length >= 3) setPlanCollapsed(true);
  }, [plannedSheets]);

  if (!streaming && !errors.length && !plannedSheets && !regenSheet) return null;
  return (
    <div className="mb-3 space-y-1">
      {streaming && (
        <div className="rounded-md px-3 py-2 flex items-center gap-2 text-[11.5px]"
          style={{ background: 'rgba(242,107,31,0.1)', border: '1px solid rgba(242,107,31,0.4)', color: '#F26B1F' }}>
          <Loader2 size={12} className="animate-spin" />
          <span className="font-semibold">{stage || 'Preparing…'}</span>
          {rasterReady && <NexusBadge variant="accent" size="xs">Raster ready</NexusBadge>}
        </div>
      )}
      {/* Manual sheet regeneration progress — visible whenever a
          user-triggered sheet regen is in flight. Kept separate from
          the primary `streaming` banner because regenerations happen
          AFTER the main stream is done (regenSheet is only ever set by
          startRegenSheet, never by the main generation flow). */}
      {regenSheet && (
        <div
          className="rounded-md px-3 py-2 flex items-center gap-2 text-[11.5px]"
          style={{ background: 'rgba(242,107,31,0.15)', border: '1px solid rgba(242,107,31,0.5)', color: '#F26B1F' }}
          data-testid="ds-regen-progress">
          <Loader2 size={12} className="animate-spin" />
          <span className="font-semibold">Sheet {regenSheet.idx}: {regenSheet.stage || 'Starting…'}</span>
          {regenSheet.buf && (
            <NexusBadge variant="accent" size="xs">
              {regenSheet.buf.length.toLocaleString()} chars
            </NexusBadge>
          )}
        </div>
      )}
      {plannedSheets && plannedSheets.length > 0 && (
        <div className="rounded-md px-3 py-2 text-[11px]"
          style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)', color: 'var(--text-primary)' }}
          data-testid="ds-sheet-plan-banner">
          <div className="flex items-center justify-between mb-1">
            <div className="font-semibold text-[10.5px] uppercase tracking-wider text-nexus-text-tertiary">
              Cognara planned {plannedSheets.length} sheet{plannedSheets.length === 1 ? '' : 's'} for this brief
            </div>
            <button
              onClick={() => setPlanCollapsed((c) => !c)}
              className="text-[10.5px] font-semibold px-2 py-0.5 rounded hover:opacity-80 transition-opacity"
              style={{ color: '#F26B1F', background: 'rgba(242,107,31,0.1)' }}
              data-testid="ds-sheet-plan-toggle">
              {planCollapsed ? 'Show details' : 'Hide details'}
            </button>
          </div>
          {!planCollapsed && (
            <ol className="list-decimal pl-4 space-y-1">
              {plannedSheets.map((s, i) => {
                const idx1 = i + 1;
                const sheetHasContent = !!(current?.sheets && current.sheets[i] &&
                  (current.sheets[i].svg || current.sheets[i].raster_b64));
                const isBusy = regenSheet?.idx === idx1 || (streaming && !sheetHasContent);
                return (
                  <li key={i} className="flex items-start gap-2 justify-between" data-testid={`ds-plan-row-${i}`}>
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold">{s.title}</span>
                      {s.description ? <span className="text-nexus-text-tertiary"> — {s.description}</span> : null}
                    </div>
                    {current?.id && onRegenSheet && (
                      <button
                        onClick={() => {
                          const override = window.prompt(
                            `${sheetHasContent ? 'Regenerate' : 'Generate'} sheet ${idx1}${s.title ? ` (${s.title})` : ''} — optional focus (blank = use plan description):`,
                            s.description || ''
                          );
                          if (override === null) return;
                          onRegenSheet(idx1, override);
                        }}
                        disabled={isBusy}
                        className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-80"
                        style={{
                          background: sheetHasContent ? 'rgba(242,107,31,0.15)' : '#F26B1F',
                          color: sheetHasContent ? '#F26B1F' : '#fff',
                          border: '1px solid rgba(242,107,31,0.4)',
                        }}
                        data-testid={`ds-plan-generate-${i}`}
                        title={sheetHasContent ? 'Regenerate this sheet' : 'Generate this sheet now'}>
                        {regenSheet?.idx === idx1 ? '…' : (sheetHasContent ? 'Regen' : 'Generate')}
                      </button>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
      {errors.map((e, i) => (
        <div key={i} className="rounded-md px-3 py-2 text-[11.5px]"
          style={{ background: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.4)', color: '#FF8E8E' }}>
          {e}
        </div>
      ))}
    </div>
  );
};

const GenerationOutput = ({ gen, streamBuf, streaming, annotations, setAnnotations, tool, setTool, tab, setTab, onShare, regenSheet, onRegenSheet, onCancelRegen }) => {
  // Compare mode — when non-null, render a second version alongside the
  // primary drawing so users can visually diff v(A) against v(B). Kept
  // local to this component so switching gens resets it automatically.
  const [compareVersion, setCompareVersion] = useState(null);   // number | null
  // Diff-highlight mode — when true and compare is active, overlays a
  // red (removed) / green (added) diff on top of each SVG so changes
  // between versions are visible at a glance. Path-level: added or
  // removed shape elements are colour-coded; unchanged geometry is
  // rendered muted.
  const [diffMode, setDiffMode] = useState('none');   // 'none' | 'split' | 'overlay'
  // Sheet index within a multi-sheet drawing. 0 = main canonical svg
  // (also always sheet 1). 1..N-1 = additional sheets from gen.sheets.
  const [activeSheet, setActiveSheet] = useState(0);
  const sheets = gen?.sheets || [];
  // Grid always visible when there are 2+ sheets. Single-sheet gens
  // hide the grid to keep the canvas full-height.
  const isMultiSheet = sheets.length >= 2;
  // Preview logic — priority: (1) picked sheet's svg for multi-sheet
  // drawings, (2) current.svg when generation is canonically saved,
  // (3) the streaming buffer during live streams, (4) fallback preview
  // from streamBuf if current came back without SVG.
  const rawSvg = (() => {
    if (isMultiSheet && activeSheet > 0) {
      return sheets[activeSheet]?.svg || '';
    }
    if (streaming) return previewSvg(streamBuf);
    if (gen?.svg) return gen.svg;
    return previewSvg(streamBuf);
  })();
  const svgToShow = fitSvgToContainer(rawSvg);
  const activeRasterB64 = isMultiSheet && activeSheet > 0
    ? sheets[activeSheet]?.raster_b64
    : gen?.raster_b64;
  // Only serve the raster URL when we actually have content — otherwise the
  // <img> hits a 401 and the empty-state overlay never renders (see iter39).
  // Guard: base64 in payload wins; else fall back to the storage URL only
  // when the generation reached 'ready' or is actively streaming.
  const rasterReady = gen?.status === 'ready' || gen?.status === 'partial' || streaming;
  const rasterUrl = activeRasterB64
    ? `data:image/png;base64,${activeRasterB64}`
    : (gen?.id && activeSheet === 0 && rasterReady ? designApi.rasterUrl(gen.id) : null);

  const downloadSvg = () => {
    if (!rawSvg) return;
    const blob = new Blob([rawSvg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cognara_${(gen?.id || 'draft').slice(0, 8)}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadRaster = () => {
    if (!rasterUrl) return;
    const a = document.createElement('a');
    a.href = rasterUrl;
    a.download = `cognara_${(gen?.id || 'draft').slice(0, 8)}.png`;
    a.click();
  };

  const hasSvg = !!svgToShow;
  const hasRaster = !!rasterUrl;
  // If the active tab doesn't have content but the other does, auto-flip.
  useEffect(() => {
    if (tab === 'svg' && !hasSvg && hasRaster) setTab('raster');
    else if (tab === 'raster' && !hasRaster && hasSvg) setTab('svg');
  }, [hasSvg, hasRaster, tab, setTab]);

  // Resolve the "compare against" SVG for the split view. compareVersion
  // is a version index — 0 = _original_svg, 1..N = versions[i-1].svg.
  const compareSvg = useMemo(() => {
    if (compareVersion === null) return null;
    const raw = compareVersion === 0
      ? (gen?._original_svg || gen?.svg || '')
      : (gen?.versions?.[compareVersion - 1]?.svg || '');
    return raw ? fitSvgToContainer(raw) : null;
  }, [compareVersion, gen]);

  const versionOptions = useMemo(() => {
    const arr = [{ value: 0, label: 'v0 · original' }];
    (gen?.versions || []).forEach((_v, i) => arr.push({ value: i + 1, label: `v${i + 1}` }));
    return arr;
  }, [gen]);

  // Compute the SVG diff whenever compare mode is active. `prev` is the
  // version the user picked to diff AGAINST; `next` is the current view.
  // Cheap enough to run inline (path/shape enumeration is O(n) in DOM
  // element count).
  const diff = useMemo(() => {
    if (compareVersion === null || !compareSvg || !svgToShow) return null;
    return diffSvgs(compareSvg, svgToShow, { mode: diffMode === 'none' ? 'overlay' : diffMode });
  }, [compareVersion, compareSvg, svgToShow, diffMode]);

  return (
    <div className="flex flex-col h-full">
      {isMultiSheet && (
        <div className="mb-3" data-testid="ds-sheet-tabs">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary font-semibold">
              Sheets · {sheets.length} generated
            </span>
            <span className="text-[10px] text-nexus-text-tertiary">
              #{activeSheet + 1} · {sheets[activeSheet]?.title || 'Untitled'}
            </span>
          </div>
          {/* Preview grid — one thumbnail per sheet. Click to switch.
              Each thumbnail has a hover-revealed Regenerate button that
              re-runs raster+SVG for JUST that sheet — much cheaper than
              regenerating the whole set. */}
          <div className="grid grid-cols-6 gap-1.5" data-testid="ds-sheet-grid">
            {sheets.map((s, i) => {
              const sheetIdx1 = i + 1;
              const isRegen = regenSheet?.idx === sheetIdx1;
              return (
              <div key={i} className="relative group" data-testid={`ds-sheet-cell-${i}`}>
                <button
                  onClick={() => setActiveSheet(i)}
                  className="relative w-full rounded-md overflow-hidden transition-all"
                  style={{
                    border: `2px solid ${activeSheet === i ? '#F26B1F' : 'var(--hairline)'}`,
                    background: '#FFFFFF',
                    aspectRatio: '1.5 / 1',
                  }}
                  title={`${s.title || `Sheet ${sheetIdx1}`}${s.description ? ' — ' + s.description : ''}`}
                  data-testid={`ds-sheet-${i}`}>
                  {s.raster_b64 ? (
                    <img
                      src={`data:image/png;base64,${s.raster_b64}`}
                      alt={s.title || `Sheet ${sheetIdx1}`}
                      className="w-full h-full object-contain"
                    />
                  ) : s.svg ? (
                    <div className="w-full h-full [&>svg]:w-full [&>svg]:h-full"
                      dangerouslySetInnerHTML={{ __html: fitSvgToContainer(s.svg) }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[10px] text-nexus-text-tertiary">Sheet {sheetIdx1}</div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 px-1 py-0.5 text-[9px] font-semibold truncate"
                    style={{ background: activeSheet === i ? '#F26B1F' : 'rgba(255,255,255,0.9)',
                              color: activeSheet === i ? '#fff' : 'var(--text-primary)' }}>
                    #{sheetIdx1} {s.title ? `· ${s.title}` : ''}
                  </div>
                </button>

                {/* Regenerate overlay — spinner while running, hover
                    button while idle. Absolutely positioned so it sits
                    on top of the thumbnail without changing its
                    layout. */}
                {isRegen ? (
                  <div
                    className="absolute inset-0 flex flex-col items-center justify-center rounded-md pointer-events-auto"
                    style={{ background: 'rgba(15,23,42,0.72)', backdropFilter: 'blur(2px)' }}
                    data-testid={`ds-sheet-regen-running-${i}`}>
                    <Loader2 size={16} className="animate-spin text-white mb-1" />
                    <span className="text-[9px] text-white font-mono text-center px-2 truncate max-w-full">
                      {regenSheet.stage}
                    </span>
                    <button
                      onClick={onCancelRegen}
                      className="mt-1 text-[9px] text-white/90 hover:text-white underline"
                      data-testid={`ds-sheet-regen-cancel-${i}`}>
                      cancel
                    </button>
                  </div>
                ) : (
                  <button
                    disabled={!!regenSheet || streaming}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!onRegenSheet) return;
                      const override = window.prompt(
                        `Regenerate sheet ${sheetIdx1}${s.title ? ` (${s.title})` : ''} — optional new focus for this panel (leave blank to re-use the original brief):`,
                        s.description || ''
                      );
                      // `null` = user cancelled the prompt. Empty string is a valid "no override".
                      if (override === null) return;
                      onRegenSheet(sheetIdx1, override);
                    }}
                    className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity rounded p-1 pointer-events-auto disabled:opacity-30 disabled:cursor-not-allowed"
                    style={{ background: 'rgba(15,23,42,0.85)', color: '#fff' }}
                    title="Regenerate this sheet"
                    data-testid={`ds-sheet-regen-btn-${i}`}>
                    <RotateCw size={11} />
                  </button>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {hasSvg && (
            <button
              onClick={() => setTab('svg')}
              className="px-3 py-1.5 rounded-md text-[11.5px] font-semibold transition-all"
              style={{
                background: tab === 'svg' ? '#F26B1F' : 'var(--layer-1)',
                color: tab === 'svg' ? '#fff' : 'var(--text-primary)',
              }}
              data-testid="ds-tab-svg">
              SVG {streaming && <span className="opacity-70">(live)</span>}
            </button>
          )}
          {hasRaster && (
            <button
              onClick={() => setTab('raster')}
              className="px-3 py-1.5 rounded-md text-[11.5px] font-semibold transition-all"
              style={{
                background: tab === 'raster' ? '#F26B1F' : 'var(--layer-1)',
                color: tab === 'raster' ? '#fff' : 'var(--text-primary)',
              }}
              data-testid="ds-tab-raster">
              Raster
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {gen?.versions?.length > 0 && !streaming && tab === 'svg' && (
            <>
              <button
                onClick={() => setCompareVersion(compareVersion === null ? (versionOptions[0]?.value ?? 0) : null)}
                className="px-2.5 py-1.5 rounded-md text-[11.5px] font-semibold flex items-center gap-1 transition-all"
                style={{
                  background: compareVersion !== null ? '#F26B1F' : 'var(--layer-1)',
                  color: compareVersion !== null ? '#fff' : 'var(--text-primary)',
                  border: '1px solid var(--hairline)',
                }}
                data-testid="ds-compare-toggle">
                <GitCompareArrows size={12} /> {compareVersion !== null ? 'Comparing' : 'Compare'}
              </button>
              {compareVersion !== null && (
                <select
                  value={compareVersion}
                  onChange={(e) => setCompareVersion(Number(e.target.value))}
                  className="px-2 py-1.5 rounded-md text-[11.5px] font-semibold outline-none"
                  style={{ background: 'var(--layer-1)', color: 'var(--text-primary)', border: '1px solid var(--hairline)' }}
                  data-testid="ds-compare-select">
                  {versionOptions.map((o) => (
                    <option key={o.value} value={o.value}>vs {o.label}</option>
                  ))}
                </select>
              )}
              {compareVersion !== null && (
                <div className="flex items-center rounded-md" style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }} data-testid="ds-diff-mode">
                  {[
                    { id: 'none',    label: 'Raw' },
                    { id: 'split',   label: 'Diff · Split' },
                    { id: 'overlay', label: 'Diff · Overlay' },
                  ].map(({ id, label }) => (
                    <button
                      key={id}
                      onClick={() => setDiffMode(id)}
                      className="px-2 py-1.5 text-[11px] font-semibold transition-all first:rounded-l-md last:rounded-r-md"
                      style={{
                        background: diffMode === id ? '#F26B1F' : 'transparent',
                        color: diffMode === id ? '#fff' : 'var(--text-primary)',
                      }}
                      data-testid={`ds-diff-${id}`}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
          {gen?.id && !streaming && (
            <NexusButton size="sm" variant={gen?.public_token ? 'primary' : 'ghost'}
              onClick={onShare} data-testid="ds-share-button">
              <Share2 size={12} /> {gen?.public_token ? 'Shared' : 'Share'}
            </NexusButton>
          )}
          {hasSvg && !streaming && (
            <NexusButton size="sm" variant="ghost" onClick={downloadSvg} data-testid="ds-download-svg">
              <Download size={12} /> SVG
            </NexusButton>
          )}
          {hasRaster && !streaming && (
            <NexusButton size="sm" variant="ghost" onClick={downloadRaster} data-testid="ds-download-raster">
              <Download size={12} /> PNG
            </NexusButton>
          )}
        </div>
      </div>

      {compareSvg && tab === 'svg' ? (
        <>
          {diff?.ok && diffMode !== 'none' && (
            <div className="mb-2 flex items-center gap-2 text-[10.5px]" data-testid="ds-diff-stats">
              <span className="px-2 py-0.5 rounded font-mono font-semibold"
                style={{ background: 'rgba(16,185,129,0.15)', color: '#059669', border: '1px solid rgba(16,185,129,0.4)' }}>
                +{diff.stats.added} added
              </span>
              <span className="px-2 py-0.5 rounded font-mono font-semibold"
                style={{ background: 'rgba(239,68,68,0.12)', color: '#B91C1C', border: '1px solid rgba(239,68,68,0.4)' }}>
                −{diff.stats.removed} removed
              </span>
              <span className="px-2 py-0.5 rounded font-mono"
                style={{ background: 'var(--layer-1)', color: 'var(--text-tertiary)', border: '1px solid var(--hairline)' }}>
                {diff.stats.unchanged} unchanged
              </span>
            </div>
          )}
          {diffMode === 'overlay' && diff?.ok ? (
            <div className="flex-1 rounded-lg overflow-hidden relative min-h-0"
              style={{ background: '#FFFFFF', border: '1px solid var(--hairline)' }}
              data-testid="ds-compare-overlay">
              <div className="absolute top-2 left-2 z-10 px-2 py-1 rounded text-[10px] font-mono font-semibold"
                style={{ background: '#F26B1F', color: '#fff' }}>
                Diff · v{compareVersion} → v{gen?.versions?.length || 0}
              </div>
              <div dangerouslySetInnerHTML={{ __html: diff.overlay }}
                className="w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg]:max-w-full [&>svg]:max-h-full flex items-center justify-center" />
            </div>
          ) : (
            <div className="flex-1 grid grid-cols-2 gap-2 min-h-0" data-testid="ds-compare-canvas">
              <div className="rounded-lg overflow-hidden relative"
                style={{ background: '#FFFFFF', border: '1px solid var(--hairline)' }}>
                <div className="absolute top-2 left-2 z-10 px-2 py-1 rounded text-[10px] font-mono font-semibold"
                  style={{ background: '#F26B1F', color: '#fff' }}>
                  Current · v{gen?.versions?.length || 0}
                  {diffMode === 'split' && diff?.ok && <span className="ml-1 opacity-80">· +{diff.stats.added}</span>}
                </div>
                <div dangerouslySetInnerHTML={{ __html: diffMode === 'split' && diff?.rightDiff ? diff.rightDiff : svgToShow }}
                  className="w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg]:max-w-full [&>svg]:max-h-full flex items-center justify-center" />
              </div>
              <div className="rounded-lg overflow-hidden relative"
                style={{ background: '#FFFFFF', border: '1px solid var(--hairline)' }}>
                <div className="absolute top-2 left-2 z-10 px-2 py-1 rounded text-[10px] font-mono font-semibold"
                  style={{ background: 'var(--layer-2)', color: 'var(--text-primary)', border: '1px solid var(--hairline)' }}>
                  Compare · v{compareVersion}
                  {diffMode === 'split' && diff?.ok && <span className="ml-1 opacity-80">· −{diff.stats.removed}</span>}
                </div>
                <div dangerouslySetInnerHTML={{ __html: diffMode === 'split' && diff?.leftDiff ? diff.leftDiff : compareSvg }}
                  className="w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg]:max-w-full [&>svg]:max-h-full flex items-center justify-center" />
              </div>
            </div>
          )}
        </>
      ) : (
        <DesignCanvas
          svg={tab === 'svg' ? svgToShow : ''}
          raster={rasterUrl}
          tab={tab}
          streaming={streaming}
          streamBufLen={streamBuf?.length || 0}
          annotations={annotations}
          setAnnotations={setAnnotations}
          tool={tool}
          setTool={setTool}
          emptyLabel={
            gen?.errors?.length
              ? gen.errors.join(' · ')
              : (gen?.status === 'failed'
                  ? 'This generation didn\'t produce any output — the stream may have been interrupted. Try generating again.'
                  : gen?.status === 'partial'
                    ? 'This generation was interrupted mid-stream. Only partial output was saved.'
                    : 'Waiting for first bytes…')
          }
        />
      )}
    </div>
  );
};

export default PortalDesignStudio;
